import React, { useState } from "react";
import { Info, ChevronDown, BookOpen } from "lucide-react";

const SCALE = [
  { grade: 80, label: "Elite", cls: "bg-[#E63946] text-white" },
  { grade: 70, label: "Plus-Plus", cls: "bg-[#E63946] text-white" },
  { grade: 60, label: "Plus", cls: "bg-[#E63946] text-white" },
  { grade: 55, label: "Above Average", cls: "bg-[#0A192F] text-white" },
  { grade: 50, label: "Average", cls: "bg-[#0A192F] text-white" },
  { grade: 45, label: "Fringe Average", cls: "bg-amber-200 text-amber-900" },
  { grade: 40, label: "Below Average", cls: "bg-amber-200 text-amber-900" },
  { grade: 30, label: "Well Below Average", cls: "bg-slate-300 text-slate-700" },
  { grade: 20, label: "Poor", cls: "bg-slate-300 text-slate-700" },
];

const TOOL_GUIDE = [
  { tool: "Hit", text: "Bat-to-ball, pitch recognition, approach. 50 projects to an MLB-average hit tool; 60+ = advanced barrel control & strike-zone discipline; 40 = swing-and-miss / approach concerns." },
  { tool: "Power", text: "Raw and game power. Anchor on peak exit velo: 50 ≈ avg game pop, 60 ≈ ~95 mph EV (older HS) with carry in games, 70+ = elite EV / over-the-fence to all fields." },
  { tool: "Run", text: "60-yard / home-to-1B times. 60-yd: 50 ≈ 6.9s, 60 ≈ 6.6s, 70 ≈ 6.4s, 80 ≈ <6.3s. Factor in-game instincts, not just raw speed." },
  { tool: "Field", text: "Hands, footwork, actions, range, instincts. 50 = reliable at the position; 60+ = plus actions/range, defensive value; 40 = likely position change." },
  { tool: "Arm", text: "Strength + accuracy + carry. OF velo anchors: 50 ≈ 85 mph, 60 ≈ 90, 70 ≈ 95+. Infield a touch lower; weight accuracy and release." },
  { tool: "Fastball", text: "Velocity + life/ride + plane. HS anchors: 50 ≈ 88-90, 55 ≈ 90-92, 60 ≈ 92-94, 70 ≈ 95+. Reward ride/run and downhill angle." },
  { tool: "Breaking Ball", text: "Curveball/slider — shape, depth, spin, finish. 50 = usable average; 60+ = swing-and-miss shape with command; 70 = wipeout, plus depth/tilt." },
  { tool: "Changeup", text: "Velo separation (8-12 mph), fade/tumble, arm-speed deception. 50 = usable; 60+ = plus deception & action that misses bats and induces weak contact." },
  { tool: "Receiving", text: "Catcher framing & presentation — quiet glove, steals strikes at the edges. 50 = average presentation; 60+ = consistently expands the zone for pitchers." },
  { tool: "Blocking", text: "Lateral mobility, recovery, balls in the dirt. 50 = average; 60+ = smothers & keeps the ball in front, controls the run game with confidence." },
  { tool: "Throwing", text: "Footwork + exchange + arm strength on stolen-base attempts. Evaluate transfer quickness and accuracy; closely tied to Pop Time." },
  { tool: "Pop Time", text: "Glove-to-glove to 2B. 50 ≈ 2.00s, 55 ≈ 1.95, 60 ≈ 1.90, 70 ≈ <1.85. Blend with arm strength and accuracy, not time alone." },
  { tool: "Overall Future Value (OFV)", text: "The headline projection grade. 70-80 = elite / perennial all-star; 60 = first-division regular; 55 = above-average regular; 50 = MLB-average regular; 45 = bench/utility; 40 = up-and-down; ≤35 = org depth. For amateurs, map to recruiting tier (D1 / D2 / JUCO / develop)." },
];

function Content() {
  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><BookOpen className="w-4 h-4" /> 20-80 Scouting Scale</div>
        <p className="text-[11px] text-slate-500 mt-1">50 = MLB average. Grade present ability and future projection.</p>
        <div className="mt-3 space-y-1">
          {SCALE.map((s) => (
            <div key={s.grade} className="flex items-center gap-3">
              <span className={`w-9 text-center py-0.5 text-xs font-data font-bold rounded-sm ${s.cls}`}>{s.grade}</span>
              <span className="text-sm text-slate-700">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-slate-200 pt-4">
        <div className="label-overline text-[#0A192F]">Tool-by-Tool Guidance</div>
        <div className="mt-3 space-y-3">
          {TOOL_GUIDE.map((t) => (
            <div key={t.tool}>
              <div className="text-xs font-bold uppercase tracking-wider text-[#0A192F]">{t.tool}</div>
              <p className="text-[12px] leading-relaxed text-slate-600 mt-0.5">{t.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ScoutingScaleReference() {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Mobile / tablet — collapsible help section (shown above the form) */}
      <div className="lg:hidden order-first mb-4" data-testid="scouting-scale-mobile">
        <button
          type="button"
          data-testid="scouting-scale-toggle"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 bg-[#0A192F] text-white px-4 py-3 rounded-sm text-xs font-semibold uppercase tracking-wider"
        >
          <span className="flex items-center gap-2"><Info className="w-4 h-4" /> 20/80 Scale Reference</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="border border-slate-200 border-t-0 rounded-b-sm bg-white p-4 max-h-[60vh] overflow-y-auto" data-testid="scouting-scale-panel-mobile">
            <Content />
          </div>
        )}
      </div>

      {/* Desktop — sticky right sidebar */}
      <aside
        className="hidden lg:block w-80 shrink-0 lg:sticky lg:top-6 self-start bg-white border border-slate-200 rounded-sm p-5 max-h-[calc(100vh-3rem)] overflow-y-auto"
        data-testid="scouting-scale-reference"
      >
        <Content />
      </aside>
    </>
  );
}
